import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Select, SelectItem } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';

const PER_PAGE_OPTIONS = [10, 20, 40, 50];

interface DictionaryPaginationBarProps {
  page: number;
  perPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  isLoading?: boolean;
  className?: string;
}

const DictionaryPaginationBar: React.FC<DictionaryPaginationBarProps> = ({
  page,
  perPage,
  totalCount,
  onPageChange,
  onPerPageChange,
  isLoading = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const start = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, totalCount);

  const handlePerPageChange = useCallback(
    (keys: Iterable<unknown>) => {
      const value = Number(Array.from(keys)[0]);
      if (!Number.isNaN(value) && value !== perPage) {
        onPageChange(1);
        onPerPageChange(value);
      }
    },
    [onPageChange, onPerPageChange, perPage]
  );

  if (totalCount === 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-lg border border-divider bg-default-50 px-3 py-2.5 text-xs text-default-600 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="shrink-0">{t('Show')}</span>
        <Select
          selectedKeys={new Set([String(perPage)])}
          onSelectionChange={handlePerPageChange}
          size="sm"
          classNames={{ base: 'w-[64px]', trigger: 'min-h-7 h-7 min-w-[64px]' }}
          aria-label={t('Per page')}
          disallowEmptySelection
          isDisabled={isLoading}
        >
          {PER_PAGE_OPTIONS.map((option) => (
            <SelectItem className='w-fit' key={String(option)} textValue={String(option)}>
              {option}
            </SelectItem>
          ))}
        </Select>
        <span className="shrink-0">{t('per page')}</span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate">
          {t('Showing {{start}}-{{end}} of {{total}}', {
            start,
            end,
            total: totalCount,
          })}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <CtyButton
            isIconOnly
            size="sm"
            variant="flat"
            onPress={() => onPageChange(Math.max(1, page - 1))}
            isDisabled={page <= 1 || isLoading}
            aria-label={t('Previous page')}
          >
            <Icon icon="mdi:chevron-left" className="w-4 h-4" />
          </CtyButton>
          <CtyButton
            isIconOnly
            size="sm"
            variant="flat"
            onPress={() => onPageChange(page + 1)}
            isDisabled={end >= totalCount || isLoading}
            aria-label={t('Next page')}
          >
            <Icon icon="mdi:chevron-right" className="w-4 h-4" />
          </CtyButton>
        </div>
      </div>
    </div>
  );
};

export default DictionaryPaginationBar;
